import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Code, Plus, Trash2, Edit, Search } from "lucide-react";
import api from "../../services/api";
import type { Skill, SkillFormData } from "../../types/admin.types";

const SkillManagementTab: React.FC = () => {
  const { t } = useTranslation(["admin", "common"]);
  const [skills, setSkills] = useState<Skill[]>([]);
  const [skillCategories, setSkillCategories] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [skillSearchTerm, setSkillSearchTerm] = useState<string>("");
  const [showSkillModal, setShowSkillModal] = useState<boolean>(false);
  const [editingSkill, setEditingSkill] = useState<Skill | null>(null);
  const [skillForm, setSkillForm] = useState<SkillFormData>({
    name: "",
    category: "",
  });
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        await Promise.all([fetchSkills(), fetchSkillCategories()]);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  useEffect(() => {
    fetchSkills();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory]);

  const fetchSkills = async (): Promise<void> => {
    try {
      const category = selectedCategory === "all" ? null : selectedCategory;
      const response = (await api.admin.getSkills(category)) as {
        data?: Skill[];
      };
      setSkills(response.data || []);
    } catch (error) {
      const err = error as {
        response?: { data?: { error?: string; message?: string } };
        message: string;
      };
      console.error("Error fetching skills:", error);
      alert(
        "Error fetching skills: " +
          (err.response?.data?.error ||
            err.response?.data?.message ||
            err.message),
      );
    }
  };

  const fetchSkillCategories = async (): Promise<void> => {
    try {
      const response = (await api.admin.getSkillCategories()) as {
        data?: string[];
      };
      setSkillCategories(response.data || []);
    } catch (error) {
      console.error("Error fetching skill categories:", error);
    }
  };

  const handleCreateSkill = (): void => {
    setEditingSkill(null);
    setSkillForm({
      name: "",
      category: "",
    });
    setShowSkillModal(true);
  };

  const handleEditSkill = (skill: Skill): void => {
    setEditingSkill(skill);
    setSkillForm({
      name: skill.name,
      category: skill.category,
    });
    setShowSkillModal(true);
  };

  const handleSaveSkill = async (
    e: React.FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    e.preventDefault();
    try {
      if (editingSkill) {
        await api.admin.updateSkill(editingSkill.id, skillForm);
        alert(t("skills.messages.updated"));
      } else {
        await api.admin.createSkill(skillForm);
        alert(t("skills.messages.created"));
      }
      setShowSkillModal(false);
      fetchSkills();
      fetchSkillCategories();
    } catch (error) {
      const err = error as {
        response?: { data?: { error?: string; message?: string } };
        message: string;
      };
      alert(
        "Error saving skill: " +
          (err.response?.data?.error ||
            err.response?.data?.message ||
            err.message),
      );
    }
  };

  const handleDeleteSkill = async (skillId: number): Promise<void> => {
    if (!window.confirm(t("skills.deleteConfirm"))) {
      return;
    }

    try {
      await api.admin.deleteSkill(skillId);
      alert(t("skills.messages.deleted"));
      fetchSkills();
    } catch (error) {
      const err = error as {
        response?: { data?: { error?: string; message?: string } };
        message: string;
      };
      alert(
        "Error deleting skill: " +
          (err.response?.data?.error ||
            err.response?.data?.message ||
            err.message),
      );
    }
  };

  const filteredSkills = skills.filter((skill) => {
    if (skillSearchTerm) {
      return (
        skill.name.toLowerCase().includes(skillSearchTerm.toLowerCase()) ||
        skill.category.toLowerCase().includes(skillSearchTerm.toLowerCase())
      );
    }
    return true;
  });

  if (loading) {
    return <div className="loading">{t("skills.loading")}</div>;
  }

  return (
    <div className="admin-section">
      <div className="section-header">
        <h2>
          <Code size={16} /> {t("skills.title")}
        </h2>
        <button className="btn-primary" onClick={handleCreateSkill}>
          <Plus size={16} /> {t("skills.addSkill")}
        </button>
      </div>

      {/* Filters */}
      <div className="filters-section" style={{ marginBottom: "20px" }}>
        <div className="search-box">
          <Search size={16} />
          <input
            type="text"
            placeholder={t("skills.searchPlaceholder")}
            value={skillSearchTerm}
            onChange={(e) => setSkillSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="filter-controls">
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="filter-select"
          >
            <option value="all">{t("skills.filters.allCategories")}</option>
            {skillCategories.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Skills Table */}
      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>{t("skills.table.name")}</th>
              <th>{t("skills.table.category")}</th>
              <th>{t("skills.table.status")}</th>
              <th>{t("skills.table.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {filteredSkills.length === 0 ? (
              <tr>
                <td
                  colSpan={4}
                  style={{
                    textAlign: "center",
                    padding: "40px",
                    color: "#999",
                  }}
                >
                  {skillSearchTerm || selectedCategory !== "all"
                    ? t("skills.noResults.filtered")
                    : t("skills.noResults.empty")}
                </td>
              </tr>
            ) : (
              filteredSkills.map((skill) => (
                <tr key={skill.id}>
                  <td>{skill.name}</td>
                  <td>{skill.category}</td>
                  <td>
                    <span
                      className={`status-badge ${skill.is_active ? "active" : "inactive"}`}
                    >
                      {skill.is_active
                        ? t("skills.table.active")
                        : t("skills.table.inactive")}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-sm btn-secondary"
                        onClick={() => handleEditSkill(skill)}
                        title={t("common:actions.edit")}
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        className="btn-sm btn-delete"
                        onClick={() => handleDeleteSkill(skill.id)}
                        title={t("common:actions.delete")}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Skill Create/Edit Modal */}
      {showSkillModal && (
        <div className="modal-overlay">
          <div
            className="modal-backdrop"
            onClick={() => setShowSkillModal(false)}
            aria-hidden="true"
          />
          <div className="modal">
            <div className="modal-header">
              <h3>
                {editingSkill
                  ? t("skills.createModal.editTitle")
                  : t("skills.createModal.title")}
              </h3>
              <button
                type="button"
                onClick={() => setShowSkillModal(false)}
                aria-label="Close modal"
                className="modal-close-btn"
              >
                &times;
              </button>
            </div>
            <form onSubmit={handleSaveSkill}>
              <div className="form-group">
                <label htmlFor="skill-name">
                  {t("skills.createModal.name")} *
                </label>
                <input
                  id="skill-name"
                  type="text"
                  value={skillForm.name}
                  onChange={(e) =>
                    setSkillForm({ ...skillForm, name: e.target.value })
                  }
                  placeholder={t("skills.createModal.namePlaceholder")}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="skill-category">
                  {t("skills.createModal.category")} *
                </label>
                <input
                  id="skill-category"
                  type="text"
                  value={skillForm.category}
                  onChange={(e) =>
                    setSkillForm({ ...skillForm, category: e.target.value })
                  }
                  placeholder={t("skills.createModal.categoryPlaceholder")}
                  list="skill-categories"
                  required
                />
                <datalist id="skill-categories">
                  {skillCategories.map((category) => (
                    <option key={category} value={category} />
                  ))}
                </datalist>
                <small
                  style={{ color: "#666", display: "block", marginTop: "4px" }}
                >
                  {t("skills.createModal.categoryHelp")}
                </small>
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowSkillModal(false)}
                >
                  {t("common:actions.cancel")}
                </button>
                <button type="submit" className="btn-primary">
                  {editingSkill
                    ? t("skills.createModal.update")
                    : t("skills.createModal.create")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SkillManagementTab;
